import React, { useContext, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import EChartsReact from 'echarts-for-react';
import AppContext from 'context/Context';
import { getColor } from 'helpers/utils';
import { formatCurrency } from './formatters';
import { aggregatePeriodFigures } from './periodAggregation';

const GAUGE_MIN = -100;
const GAUGE_MAX = 100;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const monthKey = date => date.getFullYear() * 12 + date.getMonth();

const isPeriodAtOrBeforeCurrentMonth = label => {
  const parsedDate = new Date(label);
  if (isNaN(parsedDate.getTime())) return true;
  return monthKey(parsedDate) <= monthKey(new Date());
};

const findCurrentPeriodIndex = periodLabels => {
  for (let index = periodLabels.length - 1; index >= 0; index -= 1) {
    if (isPeriodAtOrBeforeCurrentMonth(periodLabels[index])) return index;
  }
  return periodLabels.length - 1;
};

const DepartmentSpendingCurrentMonthGauge = ({
  summary,
  selectedPeriodIndexes = []
}) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const periodLabels = summary?.periodLabels || [];
  const periods = summary?.totalRow?.periods || [];

  const hasSelection = selectedPeriodIndexes.length > 0;

  const currentPeriodIndex = findCurrentPeriodIndex(periodLabels);
  const currentMonthLabel = periodLabels[currentPeriodIndex];

  const figure = hasSelection
    ? aggregatePeriodFigures(periods, selectedPeriodIndexes)
    : periods[currentPeriodIndex];

  const displayLabel = hasSelection
    ? `Selected (${selectedPeriodIndexes
        .map(index => periodLabels[index])
        .filter(Boolean)
        .join(', ')})`
    : `Current Month${currentMonthLabel ? ` (${currentMonthLabel})` : ''}`;

  const hasData = !!figure;
  const profitAndLoss = figure?.profitAndLoss ?? 0;
  const variancePercent = figure?.variancePercent ?? 0;
  const gaugeValue = clamp(variancePercent, GAUGE_MIN, GAUGE_MAX);

  const options = useMemo(
    () => ({
      series: [
        {
          type: 'gauge',
          startAngle: 90,
          endAngle: -270,
          min: GAUGE_MIN,
          max: GAUGE_MAX,
          center: ['50%', '55%'],
          radius: '90%',
          pointer: { show: false },
          progress: {
            show: true,
            overlap: false,
            roundCap: true,
            clip: false,
            itemStyle: { color: getColor('primary') }
          },
          axisLine: {
            lineStyle: {
              width: 16,
              color: [[1, isDark ? '#2c3c4f' : '#edf2f9']]
            }
          },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            width: 120,
            offsetCenter: [0, '20%'],
            fontSize: 14,
            fontWeight: 'bold',
            color: isDark ? 'white' : '#000000',
            formatter: () => `${Number(variancePercent).toFixed(2)}%`
          },
          title: {
            offsetCenter: [0, '-10%'],
            fontSize: 16,
            fontWeight: 'bold',
            color: isDark ? 'white' : '#000000'
          },
          data: [
            {
              value: gaugeValue,
              name: `£${formatCurrency(profitAndLoss)}`
            }
          ]
        }
      ]
    }),
    [gaugeValue, profitAndLoss, variancePercent, isDark]
  );

  return (
    <>
      {hasData ? (
        <EChartsReact option={options} style={{ height: '220px' }} />
      ) : (
        <div className="text-center text-muted p-5">
          No data for current month.
        </div>
      )}
      <Row className="mt-0">
        <Col className="text-center">
          <span className="text-muted">{displayLabel}</span>
        </Col>
      </Row>
    </>
  );
};

DepartmentSpendingCurrentMonthGauge.propTypes = {
  summary: PropTypes.shape({
    periodLabels: PropTypes.arrayOf(PropTypes.string),
    totalRow: PropTypes.shape({
      periods: PropTypes.arrayOf(
        PropTypes.shape({
          profitAndLoss: PropTypes.number,
          variancePercent: PropTypes.number
        })
      )
    })
  }),
  selectedPeriodIndexes: PropTypes.arrayOf(PropTypes.number)
};

export default DepartmentSpendingCurrentMonthGauge;
